import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../utils/formatUtils';
import { TrendingDown, TrendingUp, FileText, Receipt } from 'lucide-react';
import { getInviterReportsSummary } from '../../../services/invitationsService';
import useApi from '../../../hooks/useApi';

const CollaboratorsPage = () => {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const { data: summary, loading, error, callApi } = useApi(getInviterReportsSummary, {showSuccessToast: false});

  useEffect(() => {
    callApi(invitationId);
  }, [invitationId, callApi]);

  const handleNavigateToReports = (reportType) => {
    navigate(`/dashboard/collaborator/${invitationId}/${reportType}`, {
      state: { 
        inviter: summary.inviter,
        permissionLevel: summary.permission_level 
      }
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-sm">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Financial Reports - {summary.inviter.username}
        </h1>
        <p className="text-gray-600">
          Permission level: <span className="font-medium capitalize">{summary.permission_level?.replace('_', ' ')}</span>
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Expenses Card */}
        <div 
          onClick={() => handleNavigateToReports('expenses')}
          className="bg-white rounded-xl shadow-sm border-l-4 border-red-500 p-6 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Expenses</h3>
            <TrendingDown className="h-8 w-8 text-red-500" />
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(summary.expenses_summary.total_amount)}
            </p>
            <p className="text-sm text-gray-600">
              Total: {summary.expenses_summary.total_count} expenses
            </p>
          </div>
        </div>

        {/* Revenues Card */}
        <div 
          onClick={() => handleNavigateToReports('revenues')}
          className="bg-white rounded-xl shadow-sm border-l-4 border-green-500 p-6 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Revenues</h3>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(summary.revenues_summary.total_amount)}
            </p>
            <p className="text-sm text-gray-600">
              Total: {summary.revenues_summary.total_count} revenues
            </p>
          </div>
        </div>

        {/* Bills Card */}
        <div 
          onClick={() => handleNavigateToReports('bills')}
          className="bg-white rounded-xl shadow-sm border-l-4 border-yellow-500 p-6 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Bills</h3>
            <FileText className="h-8 w-8 text-yellow-500" />
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(summary.bills_summary.total_amount)}
            </p>
            <p className="text-sm text-gray-600">
              Total: {summary.bills_summary.total_count} bills
            </p>
            {summary.bills_summary.pending_count > 0 && (
              <p className="text-sm text-yellow-600">
                Pending: {summary.bills_summary.pending_count}
              </p>
            )}
            {summary.bills_summary.overdue_count > 0 && (
              <p className="text-sm text-red-600">
                Overdue: {summary.bills_summary.overdue_count}
              </p>
            )}
          </div>
        </div>

        {/* Proformas Card */}
        <div 
          onClick={() => handleNavigateToReports('proformas')}
          className="bg-white rounded-xl shadow-sm border-l-4 border-purple-500 p-6 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Proformas</h3>
            <Receipt className="h-8 w-8 text-purple-500" />
          </div>
          <div className="space-y-2">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(summary.proformas_summary.total_amount)}
            </p>
            <p className="text-sm text-gray-600">
              Total: {summary.proformas_summary.total_count} proformas
            </p>
            {summary.proformas_summary.pending_count > 0 && (
              <p className="text-sm text-yellow-600">
                Pending: {summary.proformas_summary.pending_count}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-primary-50 border border-primary-200 rounded-lg p-4">
        <p className="text-sm text-primary-800">
          Click on any card above to view and manage the respective financial reports.
        </p>
      </div>
    </div>
  );
};

export default CollaboratorsPage;